"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Bot, Sparkles, Workflow } from "lucide-react";
import { useTranslations } from "next-intl";

import { AiChatPanel } from "@/components/ai/ai-chat-panel";
import {
  AiWorkflowRunner,
  type SiteWorkflow,
} from "@/components/site/ai-workflow-runner";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import type { SiteAgentOption } from "@/templates/site/types";
import { AiResourcePrice } from "@/components/site/ai-resource-price";

export function AiWorkbench({
  loggedIn,
  agent,
  loginHref,
}: {
  loggedIn: boolean;

  agent: { id: string; name: string } | null;
  loginHref: string;
}) {
  const t = useTranslations("site.ai");
  const [agents, setAgents] = useState<SiteAgentOption[]>([]);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(
    agent,
  );
  const [workflows, setWorkflows] = useState<SiteWorkflow[]>([]);

  const [pickedWorkflow, setPickedWorkflow] = useState<SiteWorkflow | null>(
    null,
  );

  useEffect(() => {
    let alive = true;

    void apiFetch<{ agents: SiteAgentOption[] }>("/api/site/ai/agents")
      .then((data) => {
        if (alive) setAgents(data.agents);
      })

      .catch(() => {});

    void apiFetch<{ workflows: SiteWorkflow[] }>("/api/site/ai/workflows")
      .then((data) => {
        if (alive) setWorkflows(data.workflows);
      })

      .catch(() => {});

    return () => {
      alive = false;
    };
  }, []);

  const current = picked
    ? (agents.find((item) => item.id === picked.id) ?? null)
    : null;

  const agentRail = (
    <div className="space-y-1 pt-2">
      <p className="px-2 text-xs font-medium text-muted-foreground">
        {t("experts")}
      </p>

      {agents.length === 0 ? (
        <p className="px-2 py-2 text-xs text-muted-foreground">
          {t("noExperts")}
        </p>
      ) : (
        <>
          {/*
            「不指定专家」是一个显式选项而不是「取消选中」：
            用户要看得见自己现在是在跟谁聊，空状态也得是一行能点的东西
          */}
          <button
            type="button"
            onClick={() => {
              setPicked(null);
              setPickedWorkflow(null);
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
              picked === null
                ? "bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/60",
            )}
          >
            <Sparkles className="size-3.5 shrink-0" strokeWidth={1.75} />
            {t("noAgent")}
          </button>

          {agents.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setPicked({ id: item.id, name: item.name });
                setPickedWorkflow(null);
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                picked?.id === item.id
                  ? "bg-muted font-medium text-foreground"
                  : "text-muted-foreground hover:bg-muted/60",
              )}
            >
              <span className="size-5 shrink-0 overflow-hidden rounded bg-muted">
                {item.avatar ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={item.avatar} alt="" className="size-full object-cover" />
                ) : (
                  <Bot className="size-full p-0.5 text-muted-foreground" strokeWidth={1.5} />
                )}
              </span>
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
            </button>
          ))}
        </>
      )}

      {workflows.length > 0 ? (
        <>
          <p className="px-2 pt-3 text-xs font-medium text-muted-foreground">
            {t("workflows")}
          </p>

          {workflows.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setPickedWorkflow(item);
                setPicked(null);
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                pickedWorkflow?.id === item.id
                  ? "bg-muted font-medium text-foreground"
                  : "text-muted-foreground hover:bg-muted/60",
              )}
            >
              <Workflow className="size-3.5 shrink-0" strokeWidth={1.75} />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
            </button>
          ))}
        </>
      ) : null}
    </div>
  );

  const contextPane = (
    <div className="space-y-4 py-2 text-sm">
      <div>
        <p className="text-xs font-medium text-muted-foreground">
          {t("currentAgent")}
        </p>
        <p className="mt-1 text-foreground">
          {picked?.name ?? t("noAgent")}
        </p>
      </div>

      {current?.description ? (
        <p className="text-xs leading-5 text-muted-foreground">
          {current.description}
        </p>
      ) : null}

      {current && current.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {current.tags.map((tag) => (
            <span
              key={tag}
              className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      {current ? (
        <p className="text-xs text-brand">
          <AiResourcePrice free={current.price <= 0} price={current.price} membership={current.membership} mode="once" kind="agent" />
        </p>
      ) : null}

      {/* 能力清单在详情页，这里只给入口——右栏塞满会喧宾夺主 */}
      {current?.href ? (
        <Link
          href={current.href}
          className="inline-block text-xs text-muted-foreground underline-offset-4 hover:text-brand hover:underline"
        >
          {t("viewAgent")}
        </Link>
      ) : null}
    </div>
  );

  if (pickedWorkflow) {
    return (
      <div className="flex gap-4">
        <aside className="hidden w-60 shrink-0 border-r pr-4 md:block">
          {agentRail}
        </aside>

        <div className="min-w-0 flex-1 space-y-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">
              {pickedWorkflow.name}
            </h2>
            {pickedWorkflow.description ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {pickedWorkflow.description}
              </p>
            ) : null}
          </div>

          <AiWorkflowRunner

            key={pickedWorkflow.id}
            workflow={pickedWorkflow}
            loggedIn={loggedIn}
            loginHref={loginHref}
          />
        </div>
      </div>
    );
  }

  return (
    <AiChatPanel

      key={picked?.id ?? "none"}
      agent={picked}
      sideExtra={agentRail}
      contextPane={contextPane}

      loggedIn={loggedIn}
      loginHref={loginHref}
    />
  );
}
